import React, { useEffect, useState } from "react";
import "./ECLDropDown.css";
import { createPortal } from "react-dom";
import useSelectorId from "../../../utilities/hooks/useSelectorId";
import { ECLThemeImage } from "../../index";

export function closeDropDown() {
   document.body.focus();
}

export default function ECLDropDown(props) {
   //PROPS
   //trigger - JSX or text that will be put inside the trigger button
   //dropDownContent - will appear in menu
   //inModal - bool - will put the dropdown in a different portal on top of the modal portal
   //containerId - string - helps find position of container so we can adjust position of menu inside of it
   //filterText - string - when this changes, the size of the menu might change so it triggers a re-draw
   //playwright - string - id to detect landmark dropdowns with playwright
   //forceDirection - array of strings - can force a menu to open to a specific direction, ignoring the general rules of opening a dropdown
   //disableDefaultStyling - bool - if set to true, disables the default styling of buttons/links in a drop down.
   //menuWidth - number/string ("unset") - if set to a number, will force the menu to be that many pixels wide.  If set to unset, menu will not take trigger's width but respond to content's width.
   //startOpen - bool - renders the dropdown open on initialization
   //onOpen - function - run this function on open!

   const [displayDropDown, setDisplayDropDown] = useState(props.startOpen);
   const [menuStyle, setMenuStyle] = useState({
      left: "unset",
      right: "unset",
      bottom: "unset",
      top: "unset",
      visibility: "hidden",
   });
   const [readyToPosition, setReadyToPosition] = useState(false);
   const [readyToFocus, setReadyToFocus] = useState(false);
   const dropDownId = useSelectorId();
   const triggerId = dropDownId + "-trigger";
   const menuId = dropDownId + "-menu";

   const positionMenu = () => {
      const menu = document.getElementById(menuId);
      const triggerBoundingBox = document
         .getElementById(triggerId)
         .getBoundingClientRect();
      const updatedStyleObject = {
         ...menuStyle,
         left: triggerBoundingBox.left,
         top: triggerBoundingBox.bottom,
         visibility: "visible",
         width:
            props.containerId || props.menuWidth === "unset"
               ? "unset"
               : typeof props.menuWidth === "number"
                 ? props.menuWidth
                 : triggerBoundingBox.width,
      };

      //on small screens, make it take up the whole viewport
      if (window.innerWidth <= 769 || window.innerHeight <= 769) {
         updatedStyleObject.left = 0;
         updatedStyleObject.top = 0;
         updatedStyleObject.width = "100dvw";
         updatedStyleObject.height = "100dvh";
      } else {
         //Do maths for menus NOT in containers according to position of trigger in relation to the window
         if (triggerBoundingBox.top > window.innerHeight / 2) {
            //if the trigger is below 50% of the page
            //align the menu to the top of the trigger not the bottom
            const heightOfMenu = menu.clientHeight;
            const menuTop =
               triggerBoundingBox.bottom -
               triggerBoundingBox.height -
               heightOfMenu;
            updatedStyleObject.top = menuTop;
         }

         //Bound the dropdown by something, either containerID, or the universal-height-width-container div
         let bounder;
         if (props.containerId) {
            bounder = document.querySelector("#" + props.containerId);
         } else {
            bounder = document.querySelector(
               ".universal-height-width-container",
            );
         }

         //This was causing issues with items at the very top and very bottom of pages overflowing, so for now, this is controls left/right adjustment
         if (bounder) {
            const containerBoundingBox = bounder.getBoundingClientRect();
            const containerStyle = window.getComputedStyle(bounder);

            //Find mid point of trigger
            const triggerMidpoint =
               (triggerBoundingBox.right + triggerBoundingBox.left) / 2;
            //find midpoint of the container
            const containerMidpoint =
               (containerBoundingBox.right + containerBoundingBox.left) / 2;
            const containerMaxWidth =
               containerBoundingBox.right -
               containerBoundingBox.left -
               parseFloat(containerStyle.paddingLeft) -
               parseFloat(containerStyle.paddingRight);
            const triggerMaxWidth =
               triggerBoundingBox.right - triggerBoundingBox.left;
            //If midpoint of the trigger is greater than the the midpoint of the container, then adjust dropdown menu to the left
            if (triggerMidpoint > containerMidpoint) {
               //Limit width based on available space
               if (typeof props.menuWidth !== "number") {
                  updatedStyleObject.left = triggerBoundingBox.left;
                  updatedStyleObject.width =
                     containerMaxWidth < triggerMaxWidth
                        ? containerMaxWidth
                        : triggerMaxWidth;
               } else {
                  updatedStyleObject.left =
                     triggerBoundingBox.right - props.menuWidth;
                  updatedStyleObject.width = props.menuWidth;
               }
            } else {
               if (typeof props.menuWidth !== "number") {
                  updatedStyleObject.width =
                     containerMaxWidth < triggerMaxWidth
                        ? containerMaxWidth
                        : triggerMaxWidth;
               }
            }
         } else {
            if (props.menuWidth === "unset") {
               //there is a possibility that the menu could go off the right side of the page if it's wider than the trigger
               if (
                  triggerBoundingBox.left + menu.clientWidth >
                  window.innerWidth
               ) {
                  updatedStyleObject.left = "unset";
                  updatedStyleObject.right = 0;
               }
            }
         }
      }
      setMenuStyle(updatedStyleObject);
   };

   const findAllScrollableAncestors = (element) => {
      //If we don't close the menu when an ancestor scrolls, we have to do a lot more fanagling with updating the position with the scrolling
      if (!element) {
         return null;
      }
      const ancestors = [];
      let parent = element.parentElement;
      while (parent) {
         if (
            parent.scrollWidth > parent.clientWidth ||
            parent.scrollHeight > parent.clientHeight
         ) {
            ancestors.push(parent);
         }
         parent = parent.parentElement;
      }
      ancestors.push(document.documentElement);
      return ancestors;
   };

   const handleScroll = () => {
      const modal = document.getElementById("portal-modal");
      if (modal) {
         if (props.inModal) {
            setDisplayDropDown(false);
         }
      } else {
         setDisplayDropDown(false);
      }
   };

   const handleFocusOut = (e) => {
      if (
         e.currentTarget.contains(e.relatedTarget) ||
         e.relatedTarget === null ||
         document.querySelector("#ECLModal").contains(e.relatedTarget)
      ) {
         //Cool, keep dropdown open
      } else {
         if (e.relatedTarget?.id === triggerId) {
            //We're clicking the button, so let setDisplayDropDown(!displayDropDown) in the button function do the work.
         } else {
            setDisplayDropDown(false);
         }
      }
   };

   //This is a wierd one, but to get the dropdown to close after clicking an anchor element, need
   //to timeout the call to react, otherwise react router will trigger a full page refresh :shrug:
   const handleAnchorNavigation = (e) => {
      if (e.target.localName === "a") {
         setTimeout(() => {
            (setDisplayDropDown(false), 1);
         });
      }
   };

   useEffect(() => {
      if (displayDropDown) {
         setReadyToPosition(true);
         if (props.onOpen) props.onOpen();
      } else {
         //reset menuStyle to original
         setMenuStyle({
            left: "unset",
            right: "unset",
            bottom: "unset",
            top: "unset",
            visibility: "hidden",
         });
         setReadyToPosition(false);
         setReadyToFocus(false);
      }
   }, [displayDropDown]);

   useEffect(() => {
      if (readyToPosition) {
         positionMenu();
         setReadyToFocus(true);
      }
   }, [readyToPosition]);

   useEffect(() => {
      if (readyToFocus) {
         if (document.getElementById(menuId)) {
            document.getElementById(menuId).focus();
            document
               .getElementById(menuId)
               .addEventListener("focusout", handleFocusOut);
            document
               .getElementById(menuId)
               .addEventListener("click", handleAnchorNavigation);
         }
         //to sidestep menu not resizing in relation to trigger and decisions that need to be made about menu size in general
         const resizerFunction = () => {
            setDisplayDropDown(false);
         };
         window.addEventListener("resize", resizerFunction);
         //Scroll behavior needs PM decisions but this closes anything who's position would be affected by a scroll
         const scrollableAncestors = findAllScrollableAncestors(
            document.getElementById(triggerId),
         );
         if (scrollableAncestors) {
            for (const ancestor of scrollableAncestors) {
               ancestor.addEventListener("scroll", handleScroll);
            }
         }
         //Clean up event listeners
         return () => {
            if (document.getElementById(menuId)) {
               document
                  .getElementById(menuId)
                  .removeEventListener("focusout", handleFocusOut);
               document
                  .getElementById(menuId)
                  .removeEventListener("click", handleAnchorNavigation);
            }
            window.removeEventListener("resize", resizerFunction);
            const scrollableAncestors = findAllScrollableAncestors(
               document.getElementById(triggerId),
            );
            if (scrollableAncestors) {
               for (const ancestor of scrollableAncestors) {
                  ancestor.removeEventListener("scroll", handleScroll);
               }
            }
         };
      }
   }, [readyToFocus]);

   useEffect(() => {
      //when the filter text changes the height of the menu might change, so redraw
      if (document.getElementById(menuId)) {
         positionMenu();
      }
   }, [props.filterText]);

   return (
      <>
         <button
            data-testid={props.playwright + "-drop-down"}
            className={
               displayDropDown
                  ? "drop-down-trigger active-drop-down"
                  : "drop-down-trigger"
            }
            id={triggerId}
            onClick={(e) => {
               e.preventDefault();
               setDisplayDropDown(!displayDropDown);
            }}
         >
            {/* Element Can be passed in here to trigger dropdown */}
            {props.trigger}
         </button>

         {displayDropDown &&
            createPortal(
               <div className={"main-menu"}>
                  <div className="drop-down-container">
                     <div
                        tabIndex={0}
                        id={menuId}
                        data-testid={props.playwright + "-menu"}
                        className={
                           props.inModal
                              ? `portal-drop-down drop-down-in-modal ${!props.disableDefaultStyling && "default-menu-style"}`
                              : `portal-drop-down ${!props.disableDefaultStyling && "default-menu-style"}`
                        }
                        style={{ ...menuStyle }}
                     >
                        <div className="x-out-div">
                           <button onClick={closeDropDown}>
                              <ECLThemeImage fontAwesome="xmark" />
                           </button>
                        </div>
                        {props.children}
                     </div>
                  </div>
               </div>,
               document.getElementById(
                  props.inModal
                     ? "drop-down-in-modal-portal"
                     : "drop-down-portal",
               ),
            )}
      </>
   );
}
